import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ServicioSap } from 'src/app/domain/model/servicio-sap/servicio-sap';

/**
 * Tabla (dumb component) del listado de "Servicios SAP" (HU1287272).
 * Solo presentación: columnas del legacy (AdminServiciosSapViewImpl) — Código servicio,
 * Descripción del servicio, Editar y Eliminar. La paginación es manual (el paginador vive en el container).
 */
@Component({
  selector: 'app-tabla-servicios-sap',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule],
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <!-- Mobile: cards -->
    <div class="md:hidden space-y-3">
      @for (item of items(); track item.cdservicio) {
        <article
          class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
        >
          <div class="text-sm mb-1">
            <span class="text-gray-500 dark:text-gray-400"
              >Código servicio:</span
            >
            {{ item.cdservicio }}
          </div>
          <div class="text-sm mb-3">
            <span class="text-gray-500 dark:text-gray-400"
              >Descripción del servicio:</span
            >
            {{ item.dsservicio }}
          </div>
          <div class="flex flex-wrap gap-2">
            <p-button
              label="Editar"
              icon="pi pi-pencil"
              styleClass="p-button-text min-h-[44px]"
              (onClick)="editar.emit(item)"
            ></p-button>
            <p-button
              label="Eliminar"
              icon="pi pi-trash"
              severity="danger"
              styleClass="p-button-text min-h-[44px]"
              (onClick)="eliminar.emit(item)"
            ></p-button>
          </div>
        </article>
      }
    </div>

    <!-- Desktop: tabla -->
    <div class="hidden md:block">
      <p-table
        styleClass="tabla-servicios-sap p-datatable-sm p-datatable-gridlines"
        [value]="items()"
        [paginator]="false"
        [responsiveLayout]="'scroll'"
        [loading]="loading()"
        [scrollable]="true"
        scrollHeight="400px"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 10rem">
              <span class="grid-header-text" title="Código servicio"
                >Código servicio</span
              >
            </th>
            <th>
              <span class="grid-header-text" title="Descripción del servicio"
                >Descripción del servicio</span
              >
            </th>
            <th style="width: 6rem" class="text-center">Editar</th>
            <th style="width: 6rem" class="text-center">Eliminar</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
          <tr>
            <td>
              <span
                class="grid-cell-text"
                [attr.title]="tituloTabla(item.cdservicio)"
                >{{ textoTabla(item.cdservicio) }}</span
              >
            </td>
            <td>
              <span
                class="grid-cell-text"
                [attr.title]="tituloTabla(item.dsservicio)"
                >{{ textoTabla(item.dsservicio) }}</span
              >
            </td>
            <td class="text-center">
              <button
                pButton
                icon="pi pi-pencil"
                class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Editar"
                tooltipPosition="left"
                [attr.aria-label]="'Editar ' + item.cdservicio"
                (click)="editar.emit(item)"
              ></button>
            </td>
            <td class="text-center">
              <button
                pButton
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Eliminar"
                tooltipPosition="left"
                [attr.aria-label]="'Eliminar ' + item.cdservicio"
                (click)="eliminar.emit(item)"
              ></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `
})
export class TablaServiciosSapComponent {
  readonly items = input.required<ServicioSap[]>();
  readonly loading = input(false);

  readonly editar = output<ServicioSap>();
  readonly eliminar = output<ServicioSap>();

  /** Valor para mostrar en la celda; "-" cuando viene vacío o nulo (frontend-girop §8.4). */
  textoTabla(valor: string | null | undefined): string {
    const texto = (valor ?? '').trim();
    return texto || '-';
  }

  /** Texto completo para el `title` (tooltip nativo) de la celda. */
  tituloTabla(valor: string | null | undefined): string {
    return (valor ?? '').trim();
  }
}
